import { readSheet } from 'read-excel-file/browser';

export type CellValue = string | number | boolean | Date | null;
export type DataRow = Record<string, CellValue>;
export type ColumnType = 'date' | 'currency' | 'number' | 'text';
export type ColumnRole = 'date' | 'metric' | 'category' | 'identifier' | 'text';

export interface ColumnProfile {
  name: string;
  type: ColumnType;
  role: ColumnRole;
  sampleValues: string[];
  emptyCount: number;
  numericCount: number;
  dateCount: number;
  uniqueCount: number;
  detectedCurrency?: string;
}

export interface UploadedDataset {
  id: string;
  fileName: string;
  fileType: 'csv' | 'excel';
  uploadedAt: string;
  columns: string[];
  rows: DataRow[];
  profiles: ColumnProfile[];
}

export interface UserSettings {
  businessName: string;
  currency: string;
}

export interface DataQualityWarning {
  id: string;
  severity: 'info' | 'warning' | 'danger';
  title: string;
  detail: string;
}

export interface AnalyticsFilters {
  metricColumn?: string;
  dateColumn?: string;
  categoryColumn?: string;
  categoryValue?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  currency?: string;
}

export interface MetricCardData {
  label: string;
  value: string;
  detail: string;
  tone?: 'neutral' | 'positive' | 'warning';
}

export interface ChartPoint {
  label: string;
  value: number;
}

export interface PeriodComparison {
  currentLabel: string;
  previousLabel: string;
  currentValue: number;
  previousValue: number;
  changePercent: number | null;
  formattedCurrent: string;
  formattedPrevious: string;
  formattedChange: string;
}

export interface ForecastPoint extends ChartPoint {
  projected: true;
}

export interface AnalyticsSnapshot {
  dataset: UploadedDataset;
  filteredRows: DataRow[];
  metricColumn?: ColumnProfile;
  dateColumn?: ColumnProfile;
  categoryColumn?: ColumnProfile;
  metrics: MetricCardData[];
  timeSeries: ChartPoint[];
  categorySeries: ChartPoint[];
  numericBreakdown: ChartPoint[];
  comparison?: PeriodComparison;
  forecast?: ForecastPoint[];
  summary: string;
  currency: string;
}

const DATASET_STORAGE_KEY = 'gabmic.currentDataset';
const DATASET_SESSIONS_KEY = 'gabmic.datasetSessions';
const SETTINGS_STORAGE_KEY = 'gabmic.settings';
const MAX_SAVED_DATASETS = 6;

const CURRENCY_SYMBOLS: Record<string, string> = {
  '$': 'USD',
  '£': 'GBP',
  '€': 'EUR',
  '₦': 'NGN',
  '₹': 'INR',
  '¥': 'JPY',
  '₵': 'GHS',
  '₽': 'RUB',
  '₩': 'KRW',
  '₺': 'TRY',
};

const DEFAULT_SETTINGS: UserSettings = {
  businessName: '',
  currency: 'USD',
};

export const getSettings = (): UserSettings => {
  const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
  if (!stored) return DEFAULT_SETTINGS;

  try {
    return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

export const saveSettings = (settings: UserSettings) => {
  localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
};

export const saveUploadedDataset = (dataset: UploadedDataset) => {
  localStorage.setItem(DATASET_STORAGE_KEY, JSON.stringify(dataset));
  saveDatasetSession(dataset);
};

export const getStoredDataset = (): UploadedDataset | null => {
  const stored = localStorage.getItem(DATASET_STORAGE_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored) as UploadedDataset;
  } catch {
    return null;
  }
};

export const clearStoredDataset = () => {
  localStorage.removeItem(DATASET_STORAGE_KEY);
};

export const getSavedDatasets = (): UploadedDataset[] => {
  const stored = localStorage.getItem(DATASET_SESSIONS_KEY);
  if (!stored) return [];

  try {
    return (JSON.parse(stored) as UploadedDataset[])
      .filter((dataset) => dataset?.id && dataset?.fileName && Array.isArray(dataset.rows))
      .sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
  } catch {
    return [];
  }
};

export const selectSavedDataset = (datasetId: string): UploadedDataset | null => {
  const dataset = getSavedDatasets().find((savedDataset) => savedDataset.id === datasetId) ?? null;
  if (dataset) {
    localStorage.setItem(DATASET_STORAGE_KEY, JSON.stringify(dataset));
  }
  return dataset;
};

export const deleteSavedDataset = (datasetId: string) => {
  const nextDatasets = getSavedDatasets().filter((dataset) => dataset.id !== datasetId);
  localStorage.setItem(DATASET_SESSIONS_KEY, JSON.stringify(nextDatasets));

  const currentDataset = getStoredDataset();
  if (currentDataset?.id === datasetId) {
    localStorage.removeItem(DATASET_STORAGE_KEY);
  }
};

export const getDataQualityWarnings = (dataset: UploadedDataset): DataQualityWarning[] => {
  const warnings: DataQualityWarning[] = [];
  const rowCount = dataset.rows.length;
  const metricProfiles = dataset.profiles.filter((profile) => profile.role === 'metric');
  const dateProfiles = dataset.profiles.filter((profile) => profile.role === 'date');
  const categoryProfiles = dataset.profiles.filter((profile) => profile.role === 'category');

  if (metricProfiles.length === 0) {
    warnings.push({
      id: 'missing-metric',
      severity: 'danger',
      title: 'No clear metric column',
      detail: 'GabMic needs at least one numeric column such as revenue, amount, income, expense, profit, conversions, hours, or total.',
    });
  }

  if (dateProfiles.length === 0) {
    warnings.push({
      id: 'missing-date',
      severity: 'warning',
      title: 'No date column detected',
      detail: 'Charts and previous-period comparisons work best when the file includes dates.',
    });
  }

  if (categoryProfiles.length === 0) {
    warnings.push({
      id: 'missing-category',
      severity: 'info',
      title: 'No category column detected',
      detail: 'Add categories such as campaign, region, product, client segment, or department to compare performance by area.',
    });
  }

  dataset.profiles.forEach((profile) => {
    const emptyPercent = rowCount ? profile.emptyCount / rowCount : 0;
    if (profile.emptyCount > 0 && emptyPercent >= 0.12) {
      warnings.push({
        id: `empty-${profile.name}`,
        severity: emptyPercent >= 0.35 ? 'warning' : 'info',
        title: `${profile.name} has missing values`,
        detail: `${profile.emptyCount.toLocaleString()} of ${rowCount.toLocaleString()} rows are empty in this column.`,
      });
    }

    const nonEmptyCount = rowCount - profile.emptyCount;
    const numericMismatch = nonEmptyCount - profile.numericCount;
    if (
      nonEmptyCount > 0
      && profile.role === 'metric'
      && numericMismatch > 0
      && numericMismatch / nonEmptyCount >= 0.08
    ) {
      warnings.push({
        id: `numeric-mismatch-${profile.name}`,
        severity: 'warning',
        title: `${profile.name} has mixed numeric values`,
        detail: `${numericMismatch.toLocaleString()} non-empty values do not read as numbers, so totals may be lower than expected.`,
      });
    }

    const dateMismatch = nonEmptyCount - profile.dateCount;
    if (
      nonEmptyCount > 0
      && profile.role === 'date'
      && dateMismatch > 0
      && dateMismatch / nonEmptyCount >= 0.08
    ) {
      warnings.push({
        id: `date-mismatch-${profile.name}`,
        severity: 'warning',
        title: `${profile.name} has mixed date values`,
        detail: `${dateMismatch.toLocaleString()} non-empty values do not read as dates, which can affect trends and comparisons.`,
      });
    }
  });

  return warnings.slice(0, 6);
};

export const downloadRowsAsCsv = (fileName: string, columns: string[], rows: DataRow[]) => {
  const csv = [
    columns.map(escapeCsvCell).join(','),
    ...rows.map((row) => columns.map((column) => escapeCsvCell(row[column])).join(',')),
  ].join('\n');

  downloadTextFile(`${getBaseFileName(fileName)}-filtered-view.csv`, csv, 'text/csv;charset=utf-8');
};

export const downloadAnalyticsSummary = (fileName: string, snapshot: AnalyticsSnapshot) => {
  const sections = [
    `# GabMic Analytics Summary`,
    `File: ${fileName}`,
    `Rows exported: ${snapshot.filteredRows.length.toLocaleString()}`,
    `Metric: ${snapshot.metricColumn?.name ?? 'Not detected'}`,
    `Date column: ${snapshot.dateColumn?.name ?? 'Not detected'}`,
    `Category column: ${snapshot.categoryColumn?.name ?? 'Not detected'}`,
    '',
    snapshot.summary,
  ];

  if (snapshot.comparison) {
    sections.push(
      '',
      '## Previous Period',
      `${snapshot.comparison.currentLabel}: ${snapshot.comparison.formattedCurrent}`,
      `${snapshot.comparison.previousLabel}: ${snapshot.comparison.formattedPrevious}`,
      `Change: ${snapshot.comparison.formattedChange}`,
    );
  }

  if (snapshot.categorySeries.length) {
    sections.push('', '## Top Categories');
    snapshot.categorySeries.slice(0, 8).forEach((point) => {
      sections.push(`- ${point.label}: ${formatValue(point.value, snapshot.metricColumn, snapshot.currency)}`);
    });
  }

  downloadTextFile(`${getBaseFileName(fileName)}-analytics-summary.md`, sections.join('\n'), 'text/markdown;charset=utf-8');
};

export const parseBusinessFile = async (file: File): Promise<UploadedDataset> => {
  const extension = getFileExtension(file.name);
  if (!extension || !['csv', 'xlsx'].includes(extension)) {
    throw new Error('GabMic currently supports CSV and Excel .xlsx files only.');
  }

  const matrix = extension === 'csv'
    ? parseCsv(await file.text())
    : await readSheet(file) as unknown as CellValue[][];

  const headerIndex = matrix.findIndex((row) => countFilledCells(row) >= 2);
  if (headerIndex < 0) {
    throw new Error('GabMic could not find a header row with at least two columns.');
  }

  const headers = makeUniqueHeaders(matrix[headerIndex]);
  const rows = matrix
    .slice(headerIndex + 1)
    .map((row) => headers.reduce<DataRow>((record, header, index) => {
      const value = normalizeCell(row[index]);
      record[header] = value;
      return record;
    }, {}))
    .filter((row) => Object.values(row).some((value) => value !== null && String(value).trim() !== ''));

  if (rows.length === 0) {
    throw new Error('The file has column headers but no readable data rows.');
  }

  return {
    id: `${Date.now()}-${file.name}`,
    fileName: file.name,
    fileType: extension === 'csv' ? 'csv' : 'excel',
    uploadedAt: new Date().toISOString(),
    columns: headers,
    rows,
    profiles: profileColumns(headers, rows),
  };
};

export const createAnalyticsSnapshot = (
  dataset: UploadedDataset,
  filters: AnalyticsFilters = {},
): AnalyticsSnapshot => {
  const settings = getSettings();
  const dateColumn = getProfile(dataset, filters.dateColumn) ?? dataset.profiles.find((profile) => profile.role === 'date');
  const categoryColumn = getProfile(dataset, filters.categoryColumn) ?? dataset.profiles.find((profile) => profile.role === 'category');
  const metricColumn = getProfile(dataset, filters.metricColumn) ?? choosePrimaryMetric(dataset.profiles);
  const currency = filters.currency ?? metricColumn?.detectedCurrency ?? settings.currency;
  const filteredRows = filterRows(dataset.rows, filters, dateColumn, categoryColumn);
  const timeSeries = metricColumn && dateColumn ? buildTimeSeries(filteredRows, dateColumn.name, metricColumn.name) : [];
  const categorySeries = metricColumn && categoryColumn
    ? buildCategorySeries(filteredRows, categoryColumn.name, metricColumn.name)
    : [];
  const numericBreakdown = buildNumericBreakdown(filteredRows, dataset.profiles);
  const comparison = metricColumn && dateColumn
    ? buildPeriodComparison(filteredRows, dateColumn.name, metricColumn.name, metricColumn, currency)
    : undefined;
  const forecast = metricColumn && dateColumn && timeSeries.length >= 6
    ? buildSimpleForecast(timeSeries)
    : undefined;

  return {
    dataset,
    filteredRows,
    metricColumn,
    dateColumn,
    categoryColumn,
    metrics: buildMetricCards(filteredRows, dataset, metricColumn, comparison, currency),
    timeSeries,
    categorySeries,
    numericBreakdown,
    comparison,
    forecast,
    summary: buildSummary(dataset, filteredRows, metricColumn, categoryColumn, comparison, categorySeries, forecast, currency),
    currency,
  };
};

export const getCategoryValues = (dataset: UploadedDataset, categoryColumn?: string) => {
  if (!categoryColumn) return [];

  return Array.from(
    new Set(dataset.rows.map((row) => stringifyCell(row[categoryColumn])).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b));
};

export const answerQuestionFromDataset = (question: string, dataset: UploadedDataset | null): string => {
  if (!dataset) {
    return 'Upload a CSV or Excel file first, then GabMic can answer using the real rows in that file.';
  }

  const normalizedQuestion = question.trim().toLowerCase();
  const snapshot = createAnalyticsSnapshot(dataset);
  const metric = snapshot.metricColumn;
  const date = snapshot.dateColumn;
  const category = snapshot.categoryColumn;

  if (!metric) {
    return `I found ${dataset.rows.length.toLocaleString()} rows, but I could not detect a numeric metric column to calculate from. Review the columns and choose a numeric field such as revenue, amount, income, expense, profit, conversions, or total.`;
  }

  if (containsAny(normalizedQuestion, ['summarise', 'summarize', 'summary', 'overview'])) {
    return snapshot.summary;
  }

  if (containsAny(normalizedQuestion, ['how much', 'made', 'make', 'revenue', 'income', 'sales', 'earned'])) {
    if (date && containsAny(normalizedQuestion, ['this month', 'current month', 'latest month'])) {
      const latestPoint = snapshot.timeSeries[snapshot.timeSeries.length - 1];
      if (latestPoint) {
        return `For the latest month in your uploaded data, ${latestPoint.label}, ${metric.name} totals ${formatValue(latestPoint.value, metric, snapshot.currency)}.`;
      }
    }

    const total = sumRows(snapshot.filteredRows, metric.name);
    return `${metric.name} totals ${formatValue(total, metric, snapshot.currency)} across ${snapshot.filteredRows.length.toLocaleString()} uploaded rows.`;
  }

  if (containsAny(normalizedQuestion, ['changed', 'compare', 'compared', 'last month', 'previous'])) {
    if (!snapshot.comparison) {
      return 'A previous-period comparison needs a date column plus a numeric metric column. GabMic could not detect enough date-based information in this file.';
    }

    const direction = snapshot.comparison.changePercent === null
      ? 'could not be percentage-compared because the previous period is zero'
      : `${snapshot.comparison.formattedChange} versus the previous period`;

    return `${metric.name} is ${snapshot.comparison.formattedCurrent} for ${snapshot.comparison.currentLabel}, compared with ${snapshot.comparison.formattedPrevious} for ${snapshot.comparison.previousLabel}. It ${direction}.`;
  }

  if (containsAny(normalizedQuestion, ['best', 'top', 'highest', 'performed best'])) {
    const topCategory = snapshot.categorySeries[0];
    if (topCategory && category) {
      return `${topCategory.label} is the top ${category.name} by ${metric.name}, with ${formatValue(topCategory.value, metric, snapshot.currency)} in the uploaded data.`;
    }

    return `The highest ${metric.name} row is ${formatValue(getHighestRowValue(snapshot.filteredRows, metric.name), metric, snapshot.currency)}. Add or select a category column to compare areas, products, campaigns, regions, or business units.`;
  }

  if (containsAny(normalizedQuestion, ['expense', 'cost']) && containsAny(normalizedQuestion, ['increase', 'increased', 'why'])) {
    const expenseMetric = dataset.profiles.find((profile) => profile.role === 'metric' && /expense|cost|spend|paid|outflow/i.test(profile.name));
    if (!expenseMetric) {
      return 'I do not see a clear expense or cost column in the uploaded data. If one exists, rename it to something like Expense, Cost, Spend, or Amount so GabMic can analyze it.';
    }

    const expenseSnapshot = createAnalyticsSnapshot(dataset, { metricColumn: expenseMetric.name });
    const categoryText = expenseSnapshot.categorySeries[0] && expenseSnapshot.categoryColumn
      ? ` The largest ${expenseSnapshot.categoryColumn.name} contributor is ${expenseSnapshot.categorySeries[0].label} at ${formatValue(expenseSnapshot.categorySeries[0].value, expenseMetric, expenseSnapshot.currency)}.`
      : '';
    const comparisonText = expenseSnapshot.comparison
      ? ` Expenses are ${expenseSnapshot.comparison.formattedCurrent} in ${expenseSnapshot.comparison.currentLabel}, compared with ${expenseSnapshot.comparison.formattedPrevious} in ${expenseSnapshot.comparison.previousLabel}.`
      : '';

    return `${comparisonText}${categoryText} GabMic can show what increased from the uploaded data; the business reason behind the increase may need notes or context that is not in the file.`.trim();
  }

  return `Based on ${dataset.fileName}, GabMic analyzed ${dataset.rows.length.toLocaleString()} rows and detected ${dataset.columns.length} columns. ${snapshot.summary}`;
};

export const formatValue = (value: number, profile?: ColumnProfile, currency = 'USD') => {
  if (profile && (profile.type === 'currency' || /revenue|sales|income|expense|cost|profit|margin|amount|cash|total|price/i.test(profile.name))) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 2,
    }).format(value);
  }

  if (profile && /rate|percent|percentage|margin/i.test(profile.name) && Math.abs(value) <= 100) {
    return `${value.toFixed(1)}%`;
  }

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: Math.abs(value) >= 100 ? 0 : 2,
  }).format(value);
};

const getFileExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase();

const normalizeCell = (value: CellValue | undefined): CellValue => {
  if (value === undefined || value === null) return null;
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'number' || typeof value === 'boolean') return value;

  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
};

const countFilledCells = (row: CellValue[]) => row.filter((value) => normalizeCell(value) !== null).length;

const makeUniqueHeaders = (row: CellValue[]) => {
  const seen = new Map<string, number>();

  return row.map((value, index) => {
    const base = stringifyCell(value).trim() || `Column ${index + 1}`;
    const previousCount = seen.get(base) ?? 0;
    seen.set(base, previousCount + 1);
    return previousCount === 0 ? base : `${base} ${previousCount + 1}`;
  });
};

const profileColumns = (columns: string[], rows: DataRow[]): ColumnProfile[] => {
  return columns.map((column) => {
    const values = rows.map((row) => row[column]);
    const nonEmptyValues = values.filter((value) => stringifyCell(value) !== '');
    const numericValues = nonEmptyValues.map(parseNumberValue).filter((value) => value !== null);
    const dateValues = nonEmptyValues.map(parseDateValue).filter((value) => value !== null);
    const sampleValues = nonEmptyValues.slice(0, 4).map(stringifyCell);
    const uniqueCount = new Set(nonEmptyValues.map(stringifyCell)).size;
    const detectedCurrency = detectCurrency(column, nonEmptyValues);
    const currencyCount = nonEmptyValues.filter((value) => /[$£€₦₹¥₵₽₩₺]/.test(stringifyCell(value))).length;
    const minimumMatches = Math.max(2, Math.ceil(nonEmptyValues.length * 0.6));
    const looksLikeDate = dateValues.length >= minimumMatches;
    const looksLikeNumber = numericValues.length >= minimumMatches;
    const type: ColumnType = looksLikeDate
      ? 'date'
      : detectedCurrency && currencyCount > 0
        ? 'currency'
        : looksLikeNumber
          ? 'number'
          : 'text';

    return {
      name: column,
      type,
      role: inferColumnRole(column, type, uniqueCount, nonEmptyValues.length),
      sampleValues,
      emptyCount: values.length - nonEmptyValues.length,
      numericCount: numericValues.length,
      dateCount: dateValues.length,
      uniqueCount,
      detectedCurrency,
    };
  });
};

const inferColumnRole = (columnName: string, type: ColumnType, uniqueCount: number, nonEmptyCount: number): ColumnRole => {
  const normalized = columnName.toLowerCase();
  if (type === 'date') return 'date';
  if (/id|uuid|sku|code|email|phone|invoice|receipt|reference/.test(normalized)) return 'identifier';
  if (type === 'number' || type === 'currency') return 'metric';
  if (uniqueCount > 1 && uniqueCount <= Math.max(12, nonEmptyCount * 0.45)) return 'category';
  return 'text';
};

export const parseNumberValue = (value: CellValue): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const text = stringifyCell(value);
  if (!text) return null;

  const isNegative = /^\(.*\)$/.test(text) || /^-/.test(text);
  const normalized = text
    .replace(/[,$£€₦₹¥₵₽₩₺]/g, '')
    .replace(/[()%]/g, '')
    .replace(/\s/g, '')
    .trim();

  if (!/^-?\d*\.?\d+$/.test(normalized)) return null;

  const number = Number(normalized);
  if (!Number.isFinite(number)) return null;
  return isNegative ? -Math.abs(number) : number;
};

export const parseDateValue = (value: CellValue): Date | null => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'number' && value > 20000 && value < 80000) {
    const excelEpoch = Date.UTC(1899, 11, 30);
    return new Date(excelEpoch + value * 24 * 60 * 60 * 1000);
  }

  const text = stringifyCell(value);
  if (!text || !/[/-]|\d{4}|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec/i.test(text)) return null;

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};

const stringifyCell = (value: CellValue | undefined) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const detectCurrency = (columnName: string, values: CellValue[]) => {
  const joinedValues = values.map(stringifyCell).join(' ');
  const symbol = Object.keys(CURRENCY_SYMBOLS).find((candidate) => joinedValues.includes(candidate));
  if (symbol) return CURRENCY_SYMBOLS[symbol];

  const normalizedName = columnName.toLowerCase();
  if (/\busd\b|\bdollar/.test(normalizedName)) return 'USD';
  if (/\bgbp\b|\bpound/.test(normalizedName)) return 'GBP';
  if (/\beur\b|\beuro/.test(normalizedName)) return 'EUR';
  if (/\bngn\b|\bnaira/.test(normalizedName)) return 'NGN';
  if (/\binr\b|\brupee/.test(normalizedName)) return 'INR';

  return undefined;
};

const parseCsv = (text: string): CellValue[][] => {
  const rows: CellValue[][] = [];
  let row: CellValue[] = [];
  let cell = '';
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    const nextCharacter = text[index + 1];

    if (character === '"' && nextCharacter === '"') {
      cell += '"';
      index += 1;
      continue;
    }

    if (character === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (character === ',' && !inQuotes) {
      row.push(cell);
      cell = '';
      continue;
    }

    if ((character === '\n' || character === '\r') && !inQuotes) {
      if (character === '\r' && nextCharacter === '\n') {
        index += 1;
      }
      row.push(cell);
      if (row.some((value) => stringifyCell(value) !== '')) {
        rows.push(row);
      }
      row = [];
      cell = '';
      continue;
    }

    cell += character;
  }

  row.push(cell);
  if (row.some((value) => stringifyCell(value) !== '')) {
    rows.push(row);
  }

  return rows;
};

const getProfile = (dataset: UploadedDataset, name?: string) => {
  if (!name) return undefined;
  return dataset.profiles.find((profile) => profile.name === name);
};

const choosePrimaryMetric = (profiles: ColumnProfile[]) => {
  const metricProfiles = profiles.filter((profile) => profile.role === 'metric');
  return metricProfiles.find((profile) => /revenue|sales|income|amount|total|profit|expense|cost|conversion|margin|cash/i.test(profile.name))
    ?? metricProfiles[0];
};

const filterRows = (
  rows: DataRow[],
  filters: AnalyticsFilters,
  dateColumn?: ColumnProfile,
  categoryColumn?: ColumnProfile,
) => {
  const query = filters.search?.trim().toLowerCase();
  const start = filters.startDate ? new Date(filters.startDate) : null;
  const end = filters.endDate ? new Date(filters.endDate) : null;

  return rows.filter((row) => {
    if (query && !Object.values(row).some((value) => stringifyCell(value).toLowerCase().includes(query))) {
      return false;
    }

    if (categoryColumn && filters.categoryValue && stringifyCell(row[categoryColumn.name]) !== filters.categoryValue) {
      return false;
    }

    if (dateColumn && (start || end)) {
      const rowDate = parseDateValue(row[dateColumn.name]);
      if (!rowDate) return false;
      if (start && rowDate < start) return false;
      if (end) {
        const endOfDay = new Date(end);
        endOfDay.setHours(23, 59, 59, 999);
        if (rowDate > endOfDay) return false;
      }
    }

    return true;
  });
};

const sumRows = (rows: DataRow[], column: string) => {
  return rows.reduce((total, row) => total + (parseNumberValue(row[column]) ?? 0), 0);
};

const buildMetricCards = (
  rows: DataRow[],
  dataset: UploadedDataset,
  metricColumn: ColumnProfile | undefined,
  comparison: PeriodComparison | undefined,
  currency: string,
): MetricCardData[] => {
  const metricCards: MetricCardData[] = [
    {
      label: 'Rows Analyzed',
      value: rows.length.toLocaleString(),
      detail: `${dataset.columns.length} detected columns`,
    },
  ];

  if (metricColumn) {
    const total = sumRows(rows, metricColumn.name);
    const average = rows.length ? total / rows.length : 0;

    metricCards.push(
      {
        label: `Total ${metricColumn.name}`,
        value: formatValue(total, metricColumn, currency),
        detail: 'Calculated from uploaded rows',
      },
      {
        label: `Average ${metricColumn.name}`,
        value: formatValue(average, metricColumn, currency),
        detail: 'Average per row in the current filter',
      },
    );
  }

  if (comparison) {
    metricCards.push({
      label: 'Previous Period',
      value: comparison.formattedChange,
      detail: `${comparison.currentLabel} vs ${comparison.previousLabel}`,
      tone: comparison.changePercent !== null && comparison.changePercent >= 0 ? 'positive' : 'warning',
    });
  }

  return metricCards.slice(0, 4);
};

const buildTimeSeries = (rows: DataRow[], dateColumn: string, metricColumn: string) => {
  const grouped = new Map<string, { label: string; value: number; sortDate: Date }>();

  rows.forEach((row) => {
    const date = parseDateValue(row[dateColumn]);
    const value = parseNumberValue(row[metricColumn]);
    if (!date || value === null) return;

    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const existing = grouped.get(key);
    grouped.set(key, {
      label,
      value: (existing?.value ?? 0) + value,
      sortDate: new Date(date.getFullYear(), date.getMonth(), 1),
    });
  });

  return Array.from(grouped.values())
    .sort((a, b) => a.sortDate.getTime() - b.sortDate.getTime())
    .map(({ label, value }) => ({ label, value }));
};

const buildCategorySeries = (rows: DataRow[], categoryColumn: string, metricColumn: string) => {
  const grouped = new Map<string, number>();

  rows.forEach((row) => {
    const category = stringifyCell(row[categoryColumn]) || 'Uncategorized';
    const value = parseNumberValue(row[metricColumn]);
    if (value === null) return;
    grouped.set(category, (grouped.get(category) ?? 0) + value);
  });

  return Array.from(grouped.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .slice(0, 8);
};

const buildNumericBreakdown = (rows: DataRow[], profiles: ColumnProfile[]) => {
  return profiles
    .filter((profile) => profile.role === 'metric')
    .map((profile) => ({
      label: profile.name,
      value: sumRows(rows, profile.name),
    }))
    .filter((point) => point.value !== 0)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
    .slice(0, 8);
};

const buildPeriodComparison = (
  rows: DataRow[],
  dateColumn: string,
  metricColumn: string,
  metricProfile: ColumnProfile,
  currency: string,
): PeriodComparison | undefined => {
  const datedRows = rows
    .map((row) => ({ row, date: parseDateValue(row[dateColumn]) }))
    .filter((item): item is { row: DataRow; date: Date } => item.date !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (datedRows.length < 2) return undefined;

  const minDate = datedRows[0].date;
  const maxDate = datedRows[datedRows.length - 1].date;
  const midpoint = new Date((minDate.getTime() + maxDate.getTime()) / 2);
  const previousRows = datedRows.filter((item) => item.date < midpoint).map((item) => item.row);
  const currentRows = datedRows.filter((item) => item.date >= midpoint).map((item) => item.row);

  if (previousRows.length === 0 || currentRows.length === 0) return undefined;

  const previousValue = sumRows(previousRows, metricColumn);
  const currentValue = sumRows(currentRows, metricColumn);
  const changePercent = previousValue === 0 ? null : ((currentValue - previousValue) / Math.abs(previousValue)) * 100;

  return {
    currentLabel: `${formatShortDate(midpoint)} to ${formatShortDate(maxDate)}`,
    previousLabel: `${formatShortDate(minDate)} to ${formatShortDate(addDays(midpoint, -1))}`,
    currentValue,
    previousValue,
    changePercent,
    formattedCurrent: formatValue(currentValue, metricProfile, currency),
    formattedPrevious: formatValue(previousValue, metricProfile, currency),
    formattedChange: changePercent === null ? 'No baseline' : `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(1)}%`,
  };
};

const buildSimpleForecast = (timeSeries: ChartPoint[]): ForecastPoint[] | undefined => {
  if (timeSeries.length < 6) return undefined;

  const recent = timeSeries.slice(-3);
  const average = recent.reduce((total, point) => total + point.value, 0) / recent.length;
  const trend = (recent[recent.length - 1].value - recent[0].value) / Math.max(1, recent.length - 1);
  const lastLabel = timeSeries[timeSeries.length - 1].label;
  const lastDate = new Date(`1 ${lastLabel}`);

  return [1, 2, 3].map((step) => {
    const date = Number.isNaN(lastDate.getTime()) ? null : new Date(lastDate.getFullYear(), lastDate.getMonth() + step, 1);
    return {
      label: date ? date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : `Next ${step}`,
      value: Math.max(0, average + trend * step),
      projected: true,
    };
  });
};

const buildSummary = (
  dataset: UploadedDataset,
  rows: DataRow[],
  metricColumn: ColumnProfile | undefined,
  categoryColumn: ColumnProfile | undefined,
  comparison: PeriodComparison | undefined,
  categorySeries: ChartPoint[],
  forecast: ForecastPoint[] | undefined,
  currency: string,
) => {
  const opening = `GabMic analyzed ${rows.length.toLocaleString()} rows from ${dataset.fileName} and detected ${dataset.columns.length} columns.`;
  if (!metricColumn) {
    return `${opening} The file is readable, but GabMic did not detect a numeric metric column for totals, averages, or trend charts.`;
  }

  const total = sumRows(rows, metricColumn.name);
  const average = rows.length ? total / rows.length : 0;
  const categoryText = categorySeries[0] && categoryColumn
    ? ` The strongest ${categoryColumn.name} is ${categorySeries[0].label}, contributing ${formatValue(categorySeries[0].value, metricColumn, currency)}.`
    : '';
  const comparisonText = comparison
    ? ` Compared with the previous period, ${metricColumn.name} changed by ${comparison.formattedChange}.`
    : '';
  const forecastText = forecast?.[0]
    ? ` A simple forecast is available because GabMic found enough date-based history.`
    : '';

  return `${opening} ${metricColumn.name} totals ${formatValue(total, metricColumn, currency)}, with an average of ${formatValue(average, metricColumn, currency)} per row.${categoryText}${comparisonText}${forecastText}`;
};

const getHighestRowValue = (rows: DataRow[], metricColumn: string) => {
  const highest = rows.reduce((currentHighest, row) => {
    const value = parseNumberValue(row[metricColumn]);
    return value === null ? currentHighest : Math.max(currentHighest, value);
  }, Number.NEGATIVE_INFINITY);

  return Number.isFinite(highest) ? highest : 0;
};

const containsAny = (value: string, terms: string[]) => terms.some((term) => value.includes(term));

const formatShortDate = (date: Date) => date.toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const saveDatasetSession = (dataset: UploadedDataset) => {
  const sessions = getSavedDatasets().filter((savedDataset) => savedDataset.id !== dataset.id);
  const nextSessions = [dataset, ...sessions].slice(0, MAX_SAVED_DATASETS);

  try {
    localStorage.setItem(DATASET_SESSIONS_KEY, JSON.stringify(nextSessions));
  } catch {
    localStorage.setItem(DATASET_SESSIONS_KEY, JSON.stringify([dataset]));
  }
};

const escapeCsvCell = (value: CellValue | undefined) => {
  const text = stringifyCell(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
};

const downloadTextFile = (fileName: string, content: string, type: string) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const getBaseFileName = (fileName: string) => {
  return fileName.replace(/\.[^/.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'gabmic-data';
};
